import { useEffect, useState } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  Bot,
  Calendar as CalendarIcon,
  CheckCircle,
  Loader2,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { useProfile } from '@/api/profile'
import {
  useCalendars,
  useDisconnectCalendar,
  wixDisconnected,
  type ConnectedCalendar,
} from '@/api/calendars'
import { ConnectCalendarDialog } from './ConnectCalendarDialog'
import { ProviderBadge } from './ProviderIcons'
import { SquareSettingsDialog } from './SquareSettingsDialog'
import { ReconnectWixButton, WixSettingsDialog } from './WixSettingsDialog'

const PROVIDER_NAMES: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  square: 'Square',
  wix: 'Wix',
}

/** One line under the account: the service the agent books, or all of them. */
function bookingSummary(
  t: ReturnType<typeof useTranslation>['t'],
  cal: ConnectedCalendar,
  withMember: boolean,
): string | null {
  const s = cal.square
  if (s) {
    return s.service_name
      ? `${s.service_name} · ${s.service_duration_minutes} min${withMember && s.team_member_name ? ` · ${s.team_member_name}` : ''}`
      : s.services.length > 0
        ? t('manager.calendars.square.allServices', { count: s.services.length })
        : t('manager.calendars.square.toConfigure')
  }
  const w = cal.wix
  if (w) {
    return w.service_name
      ? `${w.service_name} · ${w.service_duration_minutes} min${withMember && w.staff_name ? ` · ${w.staff_name}` : ''}`
      : w.services.length > 0
        ? t('manager.calendars.wix.allServices', { count: w.services.length })
        : t('manager.calendars.wix.toConfigure')
  }
  return null
}

/** Last refusal from Square or Wix, in plain words. */
function lastErrorText(t: ReturnType<typeof useTranslation>['t'], cal: ConnectedCalendar): string | null {
  if (cal.square?.last_error) return t(`manager.calendars.square.errors.${cal.square.last_error}`)
  if (cal.wix?.last_error) {
    return t(`manager.calendars.wix.errors.${cal.wix.last_error}`, t('manager.calendars.wix.errors.wix_error'))
  }
  return null
}

export function CalendarConnectionsView() {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA'

  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data: calendars = [], isLoading } = useCalendars(companyId)
  const disconnect = useDisconnectCalendar(companyId)

  const [showAdd, setShowAdd] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ConnectedCalendar | null>(null)
  const [squareEnReglage, setSquareEnReglage] = useState<ConnectedCalendar | null>(null)
  const [wixEnReglage, setWixEnReglage] = useState<ConnectedCalendar | null>(null)
  // Posé par le callback Square ou Wix : on ouvre le réglage dès que la liste arrive.
  const [aOuvrir, setAOuvrir] = useState<{ provider: 'square' | 'wix'; id: number } | null>(null)
  useEffect(() => {
    if (aOuvrir == null) return
    const cal = calendars.find((c) => c.provider === aOuvrir.provider && c.id === aOuvrir.id)
    if (cal) {
      if (cal.provider === 'wix') setWixEnReglage(cal)
      else setSquareEnReglage(cal)
      setAOuvrir(null)
    }
  }, [calendars, aOuvrir])

  const openSettings = (cal: ConnectedCalendar) =>
    cal.provider === 'wix' ? setWixEnReglage(cal) : setSquareEnReglage(cal)
  // Square and Wix have settings; not a Wix site whose app was removed (reconnect first).
  const hasSettings = (cal: ConnectedCalendar) =>
    cal.provider === 'square' || (cal.provider === 'wix' && cal.wix?.last_error !== 'wix_app_removed')

  // Consomme les params posés par le callback OAuth (success/error + provider)
  // et affiche un toast de feedback. On strip ensuite ces params pour qu'un F5
  // ne réaffiche pas le toast et pour garder l'URL propre.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const status = searchParams.get('status')
    if (!status) return

    if (status === 'success') {
      const raw = searchParams.get('provider') || 'google'
      const provider = PROVIDER_NAMES[raw] ?? 'Google'
      toast.success(
        t('manager.calendars.connectSuccess', 'Calendrier {{provider}} connecté', {
          provider,
        }),
      )
      if (raw === 'square' || raw === 'wix') {
        const id = parseInt(searchParams.get('calendar_id') || '', 10)
        if (id) setAOuvrir({ provider: raw, id })
        const warning = searchParams.get('warning')
        if (warning === 'square_no_service' || warning === 'wix_no_service') {
          toast.warning(t(`manager.calendars.${raw}.errors.${warning}`))
        }
      }
    } else if (status === 'error') {
      const brut = searchParams.get('message') || ''
      // Les codes square_* et wix_* viennent du serveur ; on les dit en mots simples.
      const message = brut.startsWith('square_')
        ? t(`manager.calendars.square.errors.${brut}`, t('manager.calendars.connectErrorGeneric'))
        : brut.startsWith('wix_')
          ? t(`manager.calendars.wix.errors.${brut}`, t('manager.calendars.connectErrorGeneric'))
          : brut ||
          t(
            'manager.calendars.connectErrorGeneric',
            'Une erreur est survenue lors de la connexion. Réessayez.',
          )
      toast.error(
        t('manager.calendars.connectError', 'Connexion du calendrier échouée'),
        { description: message },
      )
    }

    const next = new URLSearchParams(searchParams)
    next.delete('status')
    next.delete('calendar_id')
    next.delete('provider')
    next.delete('message')
    next.delete('warning')
    next.delete('tab') // legacy Next, on nettoie au passage
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await disconnect.mutateAsync({
        provider: pendingDelete.provider,
        calendarId: pendingDelete.id,
      })
      toast.success(t('manager.calendars.toast.disconnectSuccess'))
      setPendingDelete(null)
    } catch {
      toast.error(t('manager.calendars.toast.disconnectError'))
    }
  }

  const formatSync = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString(dateLocale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('manager.calendars.never')

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            {t('manager.calendars.sectionTitle')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.calendars.sectionSubtitle')}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setShowAdd(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('manager.calendars.connectButton')}
        </Button>
      </div>

      {/* Table desktop */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wider">
              <th className="px-6 py-3">{t('manager.calendars.table.name')}</th>
              <th className="px-6 py-3">{t('manager.calendars.table.email')}</th>
              <th className="px-6 py-3">
                {t('manager.calendars.table.assignedAgents')}
              </th>
              <th className="px-6 py-3">{t('manager.calendars.table.status')}</th>
              <th className="px-6 py-3">{t('manager.calendars.table.lastSync')}</th>
              <th className="px-6 py-3 text-right">
                {t('manager.calendars.table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
                </td>
              </tr>
            ) : calendars.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12">
                  <CalendarIcon className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {t('manager.calendars.emptyState.title')}
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-500">
                    {t('manager.calendars.emptyState.subtitle')}
                  </p>
                </td>
              </tr>
            ) : (
              calendars.map((cal) => (
                <tr
                  key={`${cal.provider}-${cal.id}`}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="px-6 py-3 font-medium text-slate-900 dark:text-white">
                    <div className="flex items-center gap-2">
                      <ProviderBadge provider={cal.provider} />
                      <span className="truncate">{cal.calendar_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-600 dark:text-slate-400">
                    {cal.email}
                    {lastErrorText(t, cal) && (
                      <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                        {lastErrorText(t, cal)}
                      </div>
                    )}
                    {bookingSummary(t, cal, true) && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        {bookingSummary(t, cal, true)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    {cal.assigned_agents.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {cal.assigned_agents.map((agent) => (
                          <Badge
                            key={agent.id}
                            variant="outline"
                            className="text-xs gap-1"
                          >
                            <Bot className="h-3 w-3" />
                            {agent.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400 dark:text-slate-500">
                        {t('manager.calendars.noAgent')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    {wixDisconnected(cal) ? (
                      <Badge className="bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 hover:bg-amber-100 gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {t('manager.calendars.status.disconnected')}
                      </Badge>
                    ) : cal.is_active ? (
                      <Badge className="bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 hover:bg-green-100 gap-1">
                        <CheckCircle className="h-3 w-3" />
                        {t('manager.calendars.status.active')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {t('manager.calendars.status.inactive')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-600 dark:text-slate-400">
                    {formatSync(cal.last_sync_at)}
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {wixDisconnected(cal) && (
                      <ReconnectWixButton calendar={cal} companyId={companyId} className="mr-1" />
                    )}
                    {hasSettings(cal) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openSettings(cal)}
                        className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 gap-1.5"
                      >
                        <Settings2 className="h-4 w-4" />
                        {t('manager.calendars.settingsButton')}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPendingDelete(cal)}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 shadow-sm">
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : calendars.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-slate-500 dark:text-slate-400">
              {t('manager.calendars.emptyState.title')}
            </p>
          </div>
        ) : (
          calendars.map((cal) => (
            <div key={`${cal.provider}-${cal.id}`} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <ProviderBadge provider={cal.provider} />
                  <span className="font-medium text-sm text-slate-900 dark:text-white truncate">
                    {cal.calendar_name}
                  </span>
                </div>
                {wixDisconnected(cal) ? (
                  <Badge className="bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 text-xs">
                    {t('manager.calendars.status.disconnected')}
                  </Badge>
                ) : cal.is_active ? (
                  <Badge className="bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 text-xs">
                    {t('manager.calendars.status.active')}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    {t('manager.calendars.status.inactive')}
                  </Badge>
                )}
              </div>
              <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400">
                <div>{cal.email}</div>
                {lastErrorText(t, cal) && (
                  <div className="text-amber-700 dark:text-amber-300">{lastErrorText(t, cal)}</div>
                )}
                {bookingSummary(t, cal, false) && (
                  <div className="text-slate-400">{bookingSummary(t, cal, false)}</div>
                )}
                {cal.assigned_agents.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {cal.assigned_agents.map((agent) => (
                      <Badge
                        key={agent.id}
                        variant="outline"
                        className="text-xs gap-1"
                      >
                        <Bot className="h-3 w-3" />
                        {agent.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-slate-400">
                  {t('manager.calendars.syncLabel')}: {formatSync(cal.last_sync_at)}
                </span>
                <div className="flex flex-wrap items-center justify-end gap-1">
                {wixDisconnected(cal) && (
                  <ReconnectWixButton calendar={cal} companyId={companyId} className="h-7" />
                )}
                {hasSettings(cal) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => openSettings(cal)}
                    className="text-slate-500 h-7 px-2 gap-1.5"
                  >
                    <Settings2 className="h-4 w-4" />
                    {t('manager.calendars.settingsButton')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingDelete(cal)}
                  className="text-slate-400 hover:text-red-600 h-7 px-2"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <SquareSettingsDialog
        calendar={squareEnReglage}
        onClose={() => setSquareEnReglage(null)}
        companyId={companyId}
      />

      <WixSettingsDialog
        calendar={wixEnReglage}
        onClose={() => setWixEnReglage(null)}
        companyId={companyId}
      />

      <ConnectCalendarDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        companyId={companyId}
      />

      {/* Confirm disconnect dialog */}
      <Dialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <DialogContent
          showCloseButton={false}
          className="sm:max-w-md border-0 p-0 shadow-2xl overflow-hidden"
        >
          <div className="h-1 w-full bg-gradient-to-r from-red-500 via-red-400 to-red-500" />
          <div className="p-6 pb-0">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 dark:bg-red-950/30 ring-1 ring-red-200 dark:ring-red-900/50">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <DialogTitle className="text-xl tracking-tight">
                  {t('manager.calendars.disconnect.title')}
                </DialogTitle>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {t('manager.calendars.disconnect.confirm')}
                </p>
              </div>
            </div>
          </div>

          {pendingDelete && (
            <div className="mx-6 mt-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3">
              <div className="flex items-center gap-2">
                <ProviderBadge provider={pendingDelete.provider} />
                <span className="font-medium text-sm text-slate-900 dark:text-white">
                  {pendingDelete.calendar_name}
                </span>
                <span className="text-xs text-slate-500">
                  ({pendingDelete.email})
                </span>
              </div>
              {pendingDelete.assigned_agents.length > 0 && (
                <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  {t('manager.calendars.disconnect.agentsWarning', {
                    count: pendingDelete.assigned_agents.length,
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t bg-slate-50 dark:bg-slate-800/50 px-6 py-4 mt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPendingDelete(null)}
              disabled={disconnect.isPending}
            >
              {t('manager.calendars.modal.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
              disabled={disconnect.isPending}
              className="gap-2"
            >
              {disconnect.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {t('manager.calendars.disconnectButton')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
